/**
 * Progression + archetype data → the run-setup screen's view model.
 *
 * The same seam as `uiAdapter`: `ui/runSetup.ts` imports nothing from `meta/`,
 * so this file is where the two meet. It is also where unlock gating becomes
 * *visible* — a locked body renders dimmed with the reason it is locked, which
 * is the whole engine of a progression screen. Hiding what you have not earned
 * wastes the screen.
 */

import { ARCHETYPES, TRAVERSAL_ABILITIES, TRAVERSAL_BY_ID, getArchetype } from '../meta/archetypes';
import { KITS } from '../meta/kits';
import { buildAshShopVM } from '../meta/shop';
import { worldModifierChoices } from '../meta/worldModifiers';
import { TRACKS, UNLOCKS } from '../meta/progression';
import type { Progression } from '../meta/progression';
import { formatRunTime, isResumable } from '../meta/runState';
import type { RunState } from '../meta/runState';
import {
  attunedAbilities,
  unlockedKitIds,
  startingAbilitiesFor,
  unlockedArchetypeIds,
} from '../meta/unlockEffects';
import type {
  AbilityChoiceVM,
  KitChoiceVM,
  ArchetypeChoiceVM,
  RunSetupVM,
  WorldModeChoiceVM,
} from '../ui/runSetup';
import type { TitleVM } from '../ui/title';
import { bindingRows, resolvePadBindings, type BindOverrides, type PadBindOverrides } from '../ui/settingsModel';
import { DEFAULT_BINDINGS, DEFAULT_PAD_BINDINGS, padGlyph, type Action } from './input';

/**
 * The ability catalogue, read rather than restated — issue #118.
 *
 * These two used to be hand-written maps: nine labels and nine one-line
 * descriptions, keyed by ability id, sitting one import away from
 * `meta/archetypes.ts`'s `TRAVERSAL_ABILITIES`, which declares `name` for the
 * first and `gates` ("Level-design gate this ability opens") for the second.
 * The catalogue had **no reader at all** — `TRAVERSAL_BY_ID` was consumed only
 * by `isTraversalAbility`, which nothing called — so the authored data was dead
 * and the screen was drawing a copy of it.
 *
 * They had already drifted, which is the whole argument: the catalogue gates
 * Tether on "chasms, ceiling routes" and the copy said "chasms and ceiling
 * routes"; the catalogue gates Updraft on "vertical ascents" and the copy said
 * "vertical ascents — you make the fire", having absorbed half of a different
 * field. Two spellings of one fact, disagreeing, with the live one being the
 * copy — docs/13 §2, and #81's exact lesson.
 *
 * Order comes from `TRAVERSAL_ORDER` now, which is the authored critical path
 * (docs/02 §3.1); the map's insertion order was neither that nor alphabetical.
 *
 * The two readers are `TRAVERSAL_ABILITIES` for the attunement list, in
 * catalogue order, and `TRAVERSAL_BY_ID` for the per-archetype innate labels.
 */
const abilityLabel = (id: string): string => TRAVERSAL_BY_ID[id]?.name ?? id;

/**
 * Turn an archetype's numeric movement deltas into a sentence.
 *
 * The player is choosing *a way to move*, not a stat block — docs/02 §3.2. A
 * table of multipliers is unreadable at the moment of choosing and tells you
 * nothing about how the body will feel.
 */
function movementSummary(id: string): string[] {
  const a = getArchetype(id);
  const m = a.movement;
  const parts: string[] = [];
  const pct = (v: number | undefined) =>
    v === undefined || v === 1 ? null : `${v > 1 ? '+' : ''}${Math.round((v - 1) * 100)}%`;

  const speed = pct(m.maxRunSpeedMul);
  if (speed) parts.push(`${speed} speed`);
  const mass = pct(m.massMul);
  if (mass) parts.push(`${mass} mass`);
  const jump = pct(m.jumpHeightMul);
  if (jump) parts.push(`${jump} jump`);
  // Conditional, because the air-dash budget is zero until Airstep is found —
  // the bonus stacks on the ability rather than substituting for it. Promising
  // "+1 air dash" to a body that cannot air-dash yet is the screen lying.
  if (m.airDashBonus) {
    parts.push(
      a.innateAbilities.includes('airstep')
        ? `+${m.airDashBonus} air dash`
        : `+${m.airDashBonus} air dash once Airstep is found`,
    );
  }
  if (m.canWallSlide === false) parts.push('cannot wall-slide');
  // `canWallCling` is descriptive metadata (see `applyModifiers`'s own comment
  // on the field) — the real grant is `innateAbilities: ['cling']`, and #160
  // found a body with the flag but not the innate: the card promised clinging
  // a Segmented biomech could not do. Gated on the innate itself now, the same
  // way `airDashBonus` above is gated on `innateAbilities.includes('airstep')`.
  if (m.canWallCling && a.innateAbilities.includes('cling')) parts.push('clings to walls');
  if (m.viscosityImmunity && m.viscosityImmunity > 0.5) parts.push('ignores viscosity');

  return parts.length ? parts : ['baseline movement package'];
}

const WORLD_MODES: WorldModeChoiceVM[] = [
  {
    id: 'blended',
    name: 'Blended',
    description:
      'Every era fused into one ecosystem. Biome palettes interleave at the room ' +
      'level, so you notice you are somewhere else fifty rooms after it happened.',
    feel: 'archaeological · exploration-forward',
  },
  {
    id: 'layered',
    name: 'Layered',
    description:
      'Distinct regions with strong identity and deliberate pacing, plus ' +
      'cross-contamination — a drone in the crypt is an event, not background.',
    feel: 'structured · progression-forward',
  },
];

/** kit id -> the unlock that grants it, so a locked kit can name its price. */
const KIT_UNLOCK = new Map(
  UNLOCKS.filter((u) => u.grants.kind === 'kit').map(
    (u) => [(u.grants as { kit: string }).kit, u] as const,
  ),
);

function kitChoices(prog: Progression): KitChoiceVM[] {
  const owned = new Set(unlockedKitIds(prog));
  return KITS.map((k) => {
    const u = KIT_UNLOCK.get(k.id);
    const locked = !owned.has(k.id);
    return {
      id: k.id,
      name: k.name,
      desc: k.desc,
      locked,
      // Same argument as the archetype list: a locked row must say what earns
      // it, or the screen is just a shorter list.
      lockHint:
        !locked || !u
          ? undefined
          : u.gate === 'spend'
            ? `Locked — ${u.cost} Ash.`
            : `Locked — reach ${u.cost} ${TRACKS[u.track].name}.`,
    };
  });
}

export function buildRunSetupVM(
  prog: Progression,
  seed: number,
  shopMessage: string | null = null,
  seedLocked = false,
): RunSetupVM {
  const unlocked = new Set(unlockedArchetypeIds(prog));
  const attune = attunedAbilities(prog);
  const available = new Set(attune.available);

  const archetypes: ArchetypeChoiceVM[] = ARCHETYPES.map((a) => ({
    id: a.id,
    name: a.name,
    kind: a.kind,
    special: a.special,
    movement: movementSummary(a.id),
    innate: a.innateAbilities.map(abilityLabel),
    locked: !unlocked.has(a.id),
    // Lineage is the track that gates bodies: you unlock what killed you.
    lockHint: unlocked.has(a.id)
      ? undefined
      : 'Locked — advance Lineage by dying to the creatures aligned with this body.',
  }));

  const abilities: AbilityChoiceVM[] = TRAVERSAL_ABILITIES.map((a) => ({
    id: a.id,
    name: a.name,
    desc: a.gates,
    locked: !available.has(a.id),
    lockHint: available.has(a.id) ? undefined : 'Attune by defeating bosses.',
  }));

  return {
    archetypes,
    kits: kitChoices(prog),
    worldModes: WORLD_MODES,
    attunement: { slots: attune.slots, available: abilities },
    // Ash is meta and is spent here, between runs — scrip is the in-run
    // currency and never appears on this screen (#10, #29).
    shop: buildAshShopVM(prog, shopMessage),
    // #7 — opt-in, never rolled: a modifier that turns up unannounced is a bug
    // report, not a feature.
    modifiers: worldModifierChoices(prog),
    seed,
    seedLocked,
  };
}

// ------------------------------------------------------------------ title

/**
 * The title screen's view model.
 *
 * **There is no Continue entry, and that is a finding rather than an
 * omission.** `Game`'s constructor calls `createRunState(...)` unconditionally
 * and never reads `SaveSystem.load().run`, even though the run *is* serialised
 * and written on every autosave. So a saved run exists on disk and nothing in
 * this codebase can load it. A Continue button would therefore start a brand
 * new run wearing the word "continue", which is precisely the class of lie this
 * project keeps paying for. The three hunks that would make it real are in the
 * handoff note; until they land the entry stays off the menu.
 *
 * The summary is the same argument in the other direction: it reports only
 * counters that are genuinely maintained (`lifetime`, and the Ash balance), and
 * says nothing at all for a profile that has never finished a run rather than
 * printing a row of zeroes that looks like a bug.
 *
 * `ProfileData.playtimeMs` is still not among them, and **the reason this
 * docblock gave was wrong** — #118 found it while tracing the clock. It said
 * "nothing in the codebase ever increments it". Something does: `bankRun` adds
 * `run.elapsedMs` to it on every banked run. It read zero because *that* was
 * zero — `advanceRun` had no caller, so the field was being faithfully
 * incremented by nothing, forever. Two dead readouts, one cause, and the second
 * one wearing an explanation that sent the next reader looking in the wrong
 * place. It stays off the summary until the clock's caller lands, at which
 * point it becomes a real number and a row somebody can add on purpose.
 */
/**
 * The Continue button's one line — issue #51.
 *
 * Two jobs, and both have to fit: say *which* run this is, so pressing it is
 * not a mystery box, and say what it will not bring back, so the player is told
 * before they press it rather than after. The second half is the load-bearing
 * one. `meta/runState.ts`'s `isResumable` holds the full field-by-field
 * decision; this is the sentence a player actually reads.
 *
 * Deliberately not a summary of `runSummary()`: kills and rooms are a scoreboard
 * and this is a *choice*. Depth, time and the two currencies are what a returning
 * player needs to recognise the run as theirs.
 */
export function describeResume(run: RunState): string {
  const parts: string[] = [];
  const biomes = run.biomeOrder.length;
  if (biomes > 0) parts.push(`${biomes} biome${biomes === 1 ? '' : 's'}`);
  const rooms = run.rooms.visited.size;
  if (rooms > 0) parts.push(`${rooms} room${rooms === 1 ? '' : 's'}`);
  // Unconditional, and it took #118 to make that safe. This line used to read
  // `if (run.elapsedMs > 0)` — a workaround, not a formatting choice:
  // `advanceRun` was the only writer of `elapsedMs` and had no call site, so
  // every run's timer read 0:00.00 and the button hid the zero rather than
  // print it. Hiding a dead readout is how a dead readout survives, so the
  // guard went in the same change that gave the clock its caller. A run that
  // genuinely has no time on it is a run created and quit inside one sim step,
  // and 0:00.00 is the honest thing to say about it.
  parts.push(formatRunTime(run.elapsedMs));
  if (run.inventory.ash > 0) parts.push(`${run.inventory.ash} ash`);
  if (run.inventory.scrip > 0) parts.push(`${run.inventory.scrip} scrip`);
  return (
    `${parts.join(' · ')} — the world is rebuilt from its seed, ` +
    `so you re-enter at the start with your kit, not where you stood`
  );
}

/**
 * @param run The saved run, if there is one. Passed rather than read from a
 * `SaveSystem` here so this stays a pure view-model builder and the shell keeps
 * the single owner of the save (#51). A run that has already ended is refused
 * by `isResumable`, not by the caller — one place decides what "resumable"
 * means, and it is the same place that decides what resuming restores.
 */
export function buildTitleVM(
  prog: Progression,
  binds: BindOverrides = {},
  run: RunState | null = null,
  padBinds: PadBindOverrides = {},
): TitleVM {
  // The legend has to read off the same resolved table the run does. It used to
  // be a hand-written list, which was true right up until the settings screen
  // learned to move a key — at which point the title screen carried on saying
  // "Space jump" for a player whose jump was on Z.
  const bound = new Map(bindingRows(DEFAULT_BINDINGS, binds).map((r) => [r.action, r.key]));
  const shown = (action: string): string => bound.get(action) ?? '—';
  // The pad twin — issue #193. `TitleScreen` shows this row instead of `shown`'s
  // when a pad is the active input device, so it has to read live rebinds the
  // same way `shown` does: a player who moved jump off A must not see a stale
  // A on the one screen that cannot ask them to press it to find out.
  const padResolved = resolvePadBindings(DEFAULT_PAD_BINDINGS, padBinds);
  const padShown = (action: Action): string => {
    const found = Object.keys(padResolved).find((b) => padResolved[Number(b)] === action);
    return padGlyph(found === undefined ? null : Number(found));
  };
  const life = prog.state.lifetime;
  // Banked Ash counts as evidence of play in its own right: `lifetime.runs` is
  // only incremented by `createRunState`, so a profile that died before one was
  // ever created can still hold earned Ash — and reporting "nothing yet" over a
  // non-empty bank is the same category of lie as reporting zeroes.
  const played = life.runs > 0 || life.deaths > 0 || prog.earned('ash') > 0;

  const summary = played
    ? [
        `${life.runs} run${life.runs === 1 ? '' : 's'}`,
        `${life.deaths} death${life.deaths === 1 ? '' : 's'}`,
        `${prog.balance('ash')} ash banked`,
        `${prog.state.unlocked.length} unlocked`,
      ].join(' · ')
    : null;

  return {
    title: 'Noitroidvania',
    tagline:
      'A side-view pixel-physics roguelite. Every cell burns, dissolves, ' +
      'freezes or falls, and the world remembers what you did to it — ' +
      'until you die, which is how you get stronger.',
    summary,
    items: [
      // First when it exists, because a returning player's most likely intent is
      // the run they were already in — and absent entirely when it does not,
      // rather than present and disabled. A dead row is the failure this project
      // keeps finding; #51 was that failure with the player paying for it.
      ...(isResumable(run)
        ? ([{ id: 'continue', label: 'Continue', note: describeResume(run) }] as const)
        : []),
      {
        id: 'newRun',
        label: 'New run',
        note: 'choose a body, a kit and a world',
      },
      {
        id: 'settings',
        label: 'Settings',
        note: 'interface, contrast, overlays',
      },
      {
        id: 'lab',
        label: 'Movement lab',
        note: 'the hand-built test chamber — no enemies, no run',
      },
      {
        id: 'workshop',
        label: 'Workshop',
        note: 'the tile, mask, plan and motion editors — saving downloads the file',
      },
      {
        id: 'prologue',
        label: 'Prologue',
        // Same note whether this is a first viewing or a tenth: the hold to
        // skip is a fact about the profile (`Game.prologueSkipOffered`), not
        // about this button, and a note that claimed one or the other would
        // be wrong for half the players who read it.
        note: 'the opening strip — skippable once your profile has seen it',
      },
    ],
    // Resolved from `core/input.ts`'s DEFAULT_BINDINGS plus whatever the player
    // has moved. Worth showing here because the in-run hint line only has room
    // for six of them. Movement and the four interface keys are literals
    // because they are the ones the settings screen refuses to move, and it
    // says so on the screen where it refuses.
    controls: [
      { keys: 'A / D', pad: 'D-pad / stick', what: 'move' },
      { keys: shown('jump'), pad: padShown('jump'), what: 'jump' },
      { keys: shown('dash'), pad: padShown('dash'), what: 'dash' },
      // One button, two verbs, and they can never fire in the same frame —
      // `gripMeansGather` gives the field only the grounded, un-clung case
      // (#122). The hint said "grip a wall" for as long as that was all it did;
      // saying only half of what a button does is how a mechanic ships
      // undiscoverable.
      { keys: shown('grip'), pad: padShown('grip'), what: 'grip a wall · hold on ground: gather' },
      {
        keys: `${shown('mainAttack')} / ${shown('offAttack')}`,
        pad: `${padShown('mainAttack')} / ${padShown('offAttack')}`,
        what: 'main / off attack',
      },
      { keys: shown('ability'), pad: padShown('ability'), what: 'ability' },
      { keys: shown('parry'), pad: padShown('parry'), what: 'parry' },
      { keys: shown('interact'), pad: padShown('interact'), what: 'interact' },
      { keys: shown('swap'), pad: padShown('swap'), what: 'swap hands' },
      // Opens the same loadout panel `Tab` does, through the `inventory` action
      // `PAD_OPENS` binds it to (`ui/menuPad.ts`) — a different verb, the same
      // destination, which is why the pad column reads Back and not Tab.
      { keys: 'Tab', pad: padShown('inventory'), what: 'loadout (hold: radial)' },
      // Codex has no pad button of its own (`PAD_OPENS`' own comment: sixteen
      // buttons, seventeen actions) — reachable from the pause menu instead, so
      // the honest hint is a dash, not a made-up binding.
      { keys: 'C / M', pad: `— / ${padShown('map')}`, what: 'codex / map' },
      { keys: 'Esc', pad: padShown('pause'), what: 'pause' },
    ],
  };
}

/** Validate a chosen loadout of starting abilities against what is attuned. */
export function resolveStartAbilities(
  prog: Progression,
  archetypeId: string,
  chosen: string[],
): string[] {
  return startingAbilitiesFor(prog, archetypeId, chosen);
}
